<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { cpanelGraphQLClient } from '@/composables/default-urql-client'
import { DotsVerticalIcon, PlusIcon, UpdateIcon } from '@radix-icons/vue'
import gql from 'graphql-tag'
import { Loader, LoaderIcon } from 'lucide-vue-next'
import { ref, watch } from 'vue'
import { Button } from '../ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import Add from './cpanel/modals/Add.vue'
import BLockIP from './cpanel/modals/BLockIP.vue'
import Edit from './cpanel/modals/Edit.vue'
import UnblockIP from './cpanel/modals/UnblockIP.vue'
import WhitelistIP from './cpanel/modals/WhitelistIP.vue'

const props = defineProps<{
    active: boolean
}>()
const servers = ref([])
const loading = ref(false)
const loggingIn = ref(false)
const openAddModal = ref(false)
const openEditModal = ref(false)
const openBlockIpModal = ref(false)
const openUnblockIpModal = ref(false)
const openWhitelistIpModal = ref(false)
const selectedServer = ref(null)

watch(
    () => props.active,
    async () => {
        if (props.active) {
            await init()
        }
    }
)

const init = async () => {
    try {
        loading.value = true
        const {
            data: { servers: cpanelServers },
        } = await cpanelGraphQLClient.query(
            gql`
                query getCPanelServers {
                    servers {
                        id
                        title
                    }
                }
            `,
            {}
        )

        servers.value = cpanelServers
    } finally {
        loading.value = false
    }
}

const login = async (server) => {
    loggingIn.value = true

    try {
        const { data, error } = await cpanelGraphQLClient.query(
            gql`
                query login($serverId: String!) {
                    serverLogin(id: $serverId) {
                        url
                    }
                }
            `,
            {
                serverId: server.id,
            }
        )

        if (data) {
            const { url } = data.serverLogin

            Object.assign(document.createElement('a'), {
                target: '_blank',
                href: url,
            }).click()
        }
    } finally {
        loggingIn.value = false
    }
}

const add = (server) => {
    selectedServer.value = server
    openAddModal.value = true
}

const edit = (server) => {
    selectedServer.value = server
    openEditModal.value = true
}

const blockIp = (server) => {
    selectedServer.value = server
    openBlockIpModal.value = true
}

const unblockIp = (server) => {
    selectedServer.value = server
    openUnblockIpModal.value = true
}

const whitelistIp = (server) => {
    selectedServer.value = server
    openWhitelistIpModal.value = true
}
</script>

<template>
    <AccordionItem value="cpanel" class="border-b-0">
        <AccordionTrigger
            class="group py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800"
        >
            <div class="flex items-center justify-between flex-1">
                <div class="flex items-center">
                    <div class="w-6">
                        <FontAwesomeIcon :icon="['fab', 'cpanel']" class="size-4!" />
                    </div>
                    <div class="ml-3 text-xs">CPanel</div>
                </div>
                <div class="flex items-center">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    variant="icon"
                                    size="icon-xs"
                                    class="h-4 w-4 mr-2 hidden group-hover:flex"
                                    @click.stop="add"
                                >
                                    <PlusIcon class="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Add CPanel server</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    variant="icon"
                                    size="icon-xs"
                                    class="h-4 w-4 mr-2 hidden group-hover:flex"
                                    @click.stop="init"
                                >
                                    <UpdateIcon
                                        class="h-3 w-3"
                                        :class="{ 'animate-spin': loading }"
                                    />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Refresh</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs text-white">
            <div v-if="loading" class="flex items-center space-x-2 pl-[52px] py-2">
                <Loader class="mr-2 h-4 w-4 shrink-0 opacity-50 animate-spin" />
                <span>Loading...</span>
            </div>
            <div v-else class="flex flex-col">
                <template v-for="server in servers" :key="server.id">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <RouterLink
                                    :to="{
                                        name: 'cpanel.server.accounts.index',
                                        params: {
                                            serverId: server.id,
                                        },
                                    }"
                                    class="group flex items-center justify-between pl-[52px] py-2 pr-4 hover:bg-neutral-600/50 truncate"
                                >
                                    <div class="truncate">{{ server.title }}</div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger as-child>
                                                        <Button
                                                            variant="icon"
                                                            size="icon-xs"
                                                            class="mr-2 opacity-0 group-hover:opacity-100!"
                                                        >
                                                            <DotsVerticalIcon class="h-3 w-3" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Actions</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem @click="edit(server)"
                                                >Edit</DropdownMenuItem
                                            >
                                            <DropdownMenuItem
                                                class="flex items-center justify-between"
                                                @click="login(server)"
                                                @select.prevent=""
                                            >
                                                <span v-if="loggingIn">Logging in...</span>
                                                <span v-else>Login</span>
                                                <LoaderIcon
                                                    v-if="loggingIn"
                                                    class="h-3 w-3 animate-spin"
                                                />
                                            </DropdownMenuItem>
                                            <DropdownMenuItem @click="whitelistIp(server)"
                                                >Whitelist IP</DropdownMenuItem
                                            >
                                            <DropdownMenuItem @click="blockIp(server)"
                                                >Block IP</DropdownMenuItem
                                            >
                                            <DropdownMenuItem @click="unblockIp(server)"
                                                >Unblock IP</DropdownMenuItem
                                            >
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </RouterLink>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                                <p>{{ server.title }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </template>
            </div>
        </AccordionContent>
        <Add v-model="openAddModal" @success="init" />
        <BLockIP v-model="openBlockIpModal" :server="selectedServer" />
        <Edit v-model="openEditModal" :server="selectedServer" @success="init" />
        <UnblockIP v-model="openUnblockIpModal" :server="selectedServer" />
        <WhitelistIP v-model="openWhitelistIpModal" :server="selectedServer" />
    </AccordionItem>
</template>

<style scoped>
.router-link-exact-active {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
